<script setup>
import { computed } from 'vue'

import AppButtonX from '@dogfy/components/layout/AppButtonX.vue'
import BaseIcon from '@dogfy/components/base/BaseIcon.vue'
import BaseButton from '@dogfy/components/base/BaseButton.vue'

const isVisible = defineModel('isVisible', { type: Boolean, default: true }) // This model has a name to be explicit about its purpose.

const props = defineProps({
  icon: {
    type: String,
    default: null,
  },
  theme: {
    type: String,
    default: '',
    validator (value) {
      return ['info', 'error', 'neutral', ''].includes(value)
    },
  },
  actions: {
    type: Array,
    default: () => ([]),
  },
  isClosable: {
    type: Boolean,
    default: false,
  },
  isCompact: {
    type: Boolean,
    default: false,
  },
  isFitted: {
    type: Boolean,
    default: false,
  },
})

const buttonType = computed(() => props.theme === 'error' ? 'link-neutral small' : 'link-color small')
</script>

<template>
  <div
    v-if="isVisible"
    class="new-base-notification"
    :class="{
      [`new-base-notification--${props.theme}`]: !!props.theme,
      'new-base-notification--closable': props.isClosable,
      'new-base-notification--fitted': props.isFitted,
      'new-base-notification--compact': props.isCompact,
    }"
  >
    <BaseIcon
      v-if="props.icon"
      class="new-base-notification__icon"
      :class="{
        'new-base-notification__icon--error': props.theme === 'error',
        'new-base-notification__icon--neutral': props.theme === 'neutral',
      }"
      :icon="props.icon"
    />
    <div class="new-base-notification__container" :class="{ 'new-base-notification__container--compact': props.isCompact }">
      <p class="new-base-notification__content">
        <slot />
      </p>
      <div v-if="props.actions.length > 0" class="new-base-notification__buttonset">
        <BaseButton
          v-for="(action, index) in actions"
          :key="index"
          class="new-base-notification__button"
          :type="buttonType"
          icon="arrow-right"
          :tag="action.tag"
          :to="action.to"
          :href="action.href"
          :target="action.target"
          :data-test-id="action.dataTestId"
          @click="action.onClick"
        >
          {{ action.label }}
        </BaseButton>
      </div>
    </div>
    <AppButtonX
      v-if="props.isClosable"
      class="new-base-notification__icon"
      theme="info"
      :aria-label="$t('aria.label.closeNotification')"
      @click="isVisible = false"
    />
  </div>
</template>

<style lang="scss">
.new-base-notification {
  display: flex;
  gap: $spacer-half;
  align-items: flex-start;
  border-radius: $border-radius-sm;
  padding: $spacer $spacer-three-quarters;
  background-color: $color-feedback-info-light;
  font-size: ms(0, true);
  font-weight: $font-weight-regular;
  line-height: $font-lineheight-large;

  @include breakpoint(lg) {
    padding: $spacer;
  }

  &--closable {
    @include breakpoint(lg) {
      padding-right: $spacer-three-quarters;
    }
  }

  &--compact {
    padding: $spacer-quarter*0.5 $spacer-half $spacer-quarter*0.5 $spacer-quarter;
    border-radius: $border-radius-lg;
    font-size: ms(-1, true);
    line-height: $font-lineheight-long;
    align-items: center;
    height: 24px;

    @include breakpoint(lg) {
      font-size: ms(0, true);
      line-height: $font-lineheight-large;
    }
  }

  &--fitted {
    width: fit-content;
  }

  &--error {
    background-color: $color-feedback-error-light;
  }

  &--info {
    background-color: $color-feedback-info-light;
  }

  &--neutral {
    padding: 0;
    background-color: $color-neutral-white;
    color: $color-neutral-darkest;
  }

  &__container {
    display: flex;
    flex-direction: column;
    gap: $spacer-half;

    @include breakpoint(lg) {
      flex-direction: row;
      gap: $spacer;
      justify-content: space-between;
      align-items: center;
      width: 100%;
    }

    &--compact {
      gap: $spacer-half;
      align-items: center;
      flex-direction: row;
    }
  }

  &__content {
    strong,
    b {
      font-weight: $font-weight-semibold;
    }
  }

  &__buttonset {
    display: flex;
    flex-direction: row;
    gap: $spacer-half;
  }

  &__button {
    border: 0px;
    padding: 0px !important;

    & > svg {
      width: 20px;
      height: 20px;
    }
  }

  &__icon {
    width: 20px;
    height: 20px;
    color: $color-secondary-medium;
    flex-shrink: 0;

    &--error {
      color: $color-primary-darkest;
    }

    &--neutral {
      color: $color-neutral-darkest;
    }
  }
}
</style>
